import { Check } from 'lucide-react-native';
import { useEffect, type ComponentType } from 'react';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { DURATION, SPRING } from '@/constants/animation';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

export interface WizardStep<Key extends string = string> {
  key: Key;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

interface WizardStepsProps<Key extends string> {
  steps: readonly WizardStep<Key>[];
  current: Key;
}

/**
 * The progress row a wizard shows above its body: one circle per step, joined
 * by connectors that fill as the reader moves forward. Steps before the
 * current one show a check, the current one is filled and lifted, and the
 * rest are outlined, so the state reads by shape and weight as well as color.
 *
 * It only reports progress. Tapping a step does nothing, because each wizard
 * decides for itself which way back is safe.
 */
export function WizardSteps<Key extends string>({ steps, current }: WizardStepsProps<Key>) {
  const currentIndex = steps.findIndex((step) => step.key === current);

  const totalSteps = steps.length;

  return (
    <HStack
      className="w-full items-start gap-1 px-4 pb-2 pt-1 sm:gap-2"
      accessibilityLabel={`Step ${currentIndex + 1} of ${totalSteps}: ${
        steps[currentIndex]?.label ?? ''
      }`}>
      {steps.flatMap((step, index) => {
        const status = pickStepStatus(index, currentIndex);

        const badge = (
          <WizardStepBadge
            key={step.key}
            step={step}
            status={status}
            index={index}
            total={totalSteps}
          />
        );

        const isLast = index === totalSteps - 1;

        if (isLast) {
          return [badge];
        }

        return [
          badge,
          <WizardConnector key={`${step.key}-connector`} active={index < currentIndex} />,
        ];
      })}
    </HStack>
  );
}

interface WizardStepBadgeProps {
  step: WizardStep;
  status: 'completed' | 'active' | 'upcoming';
  index: number;
  total: number;
}

function WizardStepBadge({ step, status, index, total }: WizardStepBadgeProps) {
  const isCompleted = status === 'completed';

  const isActive = status === 'active';

  /**
   * Three stacked layers produce a smooth, three-state fill:
   *
   *   - Base: `border-outline-300` + `bg-background-0` — always
   *     visible, defines the "upcoming" look.
   *   - Completed overlay: `primary[200]` (soft brand acknowledgement),
   *     opacity animates 0 → 1 the moment the step finishes. Stays at 1
   *     when the step is active too, sitting under the active fill.
   *   - Active overlay: `primary[700]` (full brand emphasis), opacity
   *     animates 0 → 1 only while the step is the current phase, then
   *     fades back to 0 as the wizard moves on — revealing the
   *     completed fill underneath. Result: upcoming = neutral outline,
   *     active = full brand, completed = soft brand. The active fill
   *     uses `primary-700` rather than `primary-500` so the white step
   *     glyph clears WCAG AAA contrast at the `size="xs"` (14px)
   *     stroke weight.
   *
   * Colors come from `useColors()` (theme-aware: LIGHT_COLORS /
   * DARK_COLORS swap by current scheme) rather than NativeWind `bg-*`
   * classes on `<Animated.View>` — the className path doesn't reliably
   * apply background colors to Reanimated's animated wrapper, so the
   * three-layer overlay collapses to plain white circles. Inline
   * `style.backgroundColor` sidesteps that entirely.
   *
   * Scale uses a `withSpring` so the active step gently lifts to 1.05x.
   * Small enough not to shift the row, large enough to mark "you are
   * here" against the now-softer completed circles.
   */
  const { colors } = useColors();

  const scaleValue = useSharedValue(isActive ? 1.05 : 1);

  useEffect(() => {
    scaleValue.value = withSpring(isActive ? 1.05 : 1, SPRING.gentle);
  }, [isActive, scaleValue]);

  const circleTransformStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scaleValue.value }],
  }));

  const iconColorClassName = pickIconColorClassName(status);

  const labelClassName = pickLabelClassName(status);

  // Completed steps render a Check glyph so "done" reads at a glance;
  // active and upcoming render the step's own icon.
  const IconComponent = isCompleted ? Check : step.icon;

  return (
    <VStack
      className="items-center gap-1"
      {...getAccessibilityProps({
        label: `${step.label}, step ${index + 1} of ${total}, ${pickStatusLabel(status)}`,
      })}>
      <Animated.View style={circleTransformStyle} aria-hidden>
        <HStack
          className="relative h-7 w-7 items-center justify-center rounded-full border border-outline-300"
          style={{ backgroundColor: pickBadgeBackgroundColor(status, colors) }}>
          <Icon as={IconComponent} className={iconColorClassName} size="xs" />
        </HStack>
      </Animated.View>
      <Text className={labelClassName}>{step.label}</Text>
    </VStack>
  );
}

type WizardStepStatus = 'completed' | 'active' | 'upcoming';

function pickStepStatus(index: number, currentIndex: number): WizardStepStatus {
  if (index < currentIndex) {
    return 'completed';
  }

  if (index === currentIndex) {
    return 'active';
  }

  return 'upcoming';
}

/**
 * Stepper labels sit below the header title in weight and contrast so
 * the title remains the primary anchor; the stepper just tracks
 * progress. The active label renders heavier than completed/upcoming
 * so the current step is signalled by both weight and contrast (not
 * color alone), keeping WCAG 1.4.1 satisfied.
 */
function pickLabelClassName(status: WizardStepStatus): string {
  if (status === 'active') {
    return 'text-2xs font-semibold text-typography-900 sm:text-xs';
  }

  return 'text-2xs font-medium text-typography-500 sm:text-xs';
}

/**
 * Icon tint for the step glyph. Mirrors the background fill state so
 * the icon stays legible against each layer in both light and dark
 * themes. The primary scale inverts between themes (primary-700 is
 * dark green in light mode but light sage in dark mode), so we pair
 * each fill with the auto-inverting typography token whose lightness
 * runs opposite to the fill:
 *
 *   - Active: typography-50 on primary-700.
 *     Light: near-white on dark green (~11:1).
 *     Dark: near-black on light sage (~7:1).
 *   - Completed: primary-950 on primary-200.
 *     Light: near-black on soft sage (~9:1).
 *     Dark: pale green on dark green (~9:1).
 *   - Upcoming: muted typography on the neutral background-0 base.
 */
function pickBadgeBackgroundColor(
  status: WizardStepStatus,
  colors: ReturnType<typeof useColors>['colors']
): string {
  if (status === 'active') {
    return colors.primary[700];
  }

  if (status === 'completed') {
    return colors.primary[200];
  }

  return colors.background[0];
}

function pickIconColorClassName(status: WizardStepStatus): string {
  if (status === 'active') {
    return 'text-typography-50';
  }

  if (status === 'completed') {
    return 'text-primary-950';
  }

  return 'text-typography-500';
}

function pickStatusLabel(status: WizardStepStatus): string {
  if (status === 'active') {
    return 'in progress';
  }

  if (status === 'completed') {
    return 'completed';
  }

  return 'upcoming';
}

function WizardConnector({ active }: { active: boolean }) {
  /**
   * The connector renders two layers: a muted `outline[200]` base
   * that's always full width, and a `primary[500]` fill that
   * animates its `width` percentage from 0% to 100% when the step
   * before it is completed. The animated width is what makes the
   * "step n + 1 has been reached" transition feel like the progress
   * is flowing forward rather than snapping.
   *
   * Colors come from `useColors()` (theme-aware) rather than
   * NativeWind `bg-*` classes on the `<Animated.View>` because the
   * className path doesn't reliably apply background colors to
   * Reanimated's animated wrapper — see `WizardStepBadge` for the
   * same constraint.
   *
   * The outer wrapper is `h-7` so the connector vertically aligns
   * with the 28-px circle's center even though the badge column
   * extends below (label sits under the circle). Without this the
   * line would render at the top of the wizard row.
   */
  const { colors } = useColors();

  const fillPercent = useSharedValue(active ? 100 : 0);

  useEffect(() => {
    fillPercent.value = withTiming(active ? 100 : 0, { duration: DURATION.normal });
  }, [active, fillPercent]);

  const fillStyle = useAnimatedStyle(() => ({
    width: `${fillPercent.value}%`,
  }));

  return (
    <HStack className="h-7 flex-1 items-center" aria-hidden>
      <HStack className="relative h-0.5 w-full overflow-hidden bg-outline-200">
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              backgroundColor: colors.primary[500],
            },
            fillStyle,
          ]}
        />
      </HStack>
    </HStack>
  );
}
